/**
 * Centralized CRA env (REACT_APP_*).
 *
 * Deux sources, dans cet ordre :
 *   1. `window.__ENV__` — config d'EXÉCUTION, écrite dans `/env.js` au démarrage
 *      du conteneur (docker/40-runtime-env.sh) à partir des variables REACT_APP_*
 *      du pod. Une même image sert ainsi dev, staging, prod et les éphémères.
 *   2. `process.env` — valeurs figées par CRA au build (dev local, `.env`,
 *      déploiement rsync du droplet de test).
 * Details : docs/ENVIRONMENTS.md et .env.example.
 * Ne jamais committer de secrets reels - utiliser .env / .env.local localement
 * et les variables CI pour les deploiements.
 *
 * Fail soft en developpement (warnings console).
 * En production build, les variables critiques manquantes sont signalees
 * clairement (console.error) sans planter le boot shell.
 */

const isProdBuild = process.env.NODE_ENV === "production";
const isDev = process.env.NODE_ENV === "development";

function runtimeEnv() {
  if (typeof window === "undefined") return {};
  const env = window.__ENV__;
  return env && typeof env === "object" ? env : {};
}

/**
 * Valeur brute d'une variable : config d'exécution d'abord, build ensuite.
 * Une valeur d'exécution vide compte comme absente.
 */
export function readEnv(key) {
  const runtime = runtimeEnv()[key];
  if (runtime != null && String(runtime).trim() !== "") return runtime;
  return process.env[key];
}

function readTrimmed(key) {
  const value = readEnv(key);
  if (value == null) return "";
  return String(value).trim();
}

function readBool(key, defaultTrue = true) {
  const raw = readEnv(key);
  if (raw == null || String(raw).trim() === "") {
    return defaultTrue;
  }
  return String(raw).toLowerCase() !== "false";
}

function warnMissing(key, hint) {
  const message = `[env] ${key} manquant. ${hint || ""}`.trim();
  if (isProdBuild) {
    console.error(message);
  } else if (isDev) {
    console.warn(message);
  }
}

/** Backend API base URL (avec suffixe /api). */
export const API_URL =
  readTrimmed("REACT_APP_API_URL") ||
  readTrimmed("VITE_API_URL") ||
  "http://localhost:8000/api";

/** Google OAuth client id (public). */
export const GOOGLE_CLIENT_ID = readTrimmed("REACT_APP_GOOGLE_CLIENT_ID");

/** TinyMCE API key (public cloud key). */
export const TINYMCE_API_KEY = readTrimmed("REACT_APP_TINYMCE_KEY");

/** Google reCAPTCHA v3 site key (public). */
export const RECAPTCHA_SITE_KEY = readTrimmed("REACT_APP_RECAPTCHA_SITE_KEY");

/** Online payment provider: "fedapay" | "kkiapay". */
export const PAYMENT_ONLINE_PROVIDER = (() => {
  const raw = readTrimmed("REACT_APP_PAYMENT_ONLINE_PROVIDER").toLowerCase();
  if (raw === "fedapay") return "fedapay";
  return "kkiapay";
})();

export const KKIAPAY_PUBLIC_KEY = readTrimmed("REACT_APP_KKIAPAY_PUBLIC_KEY");
export const KKIAPAY_SANDBOX = readBool("REACT_APP_KKIAPAY_SANDBOX", true);

export const FEDAPAY_PUBLIC_KEY = readTrimmed("REACT_APP_FEDAPAY_PUBLIC_KEY");
export const FEDAPAY_SANDBOX = readBool("REACT_APP_FEDAPAY_SANDBOX", true);
export const FEDAPAY_ENVIRONMENT = FEDAPAY_SANDBOX ? "sandbox" : "live";

/** Algolia (recherche produits — clés search-only publiques). */
export const ALGOLIA_APP_ID = readTrimmed("REACT_APP_ALGOLIA_APP_ID");
export const ALGOLIA_SEARCH_KEY = readTrimmed("REACT_APP_ALGOLIA_SEARCH_KEY");
export const ALGOLIA_PRODUCTS_INDEX =
  readTrimmed("REACT_APP_ALGOLIA_PRODUCTS_INDEX") || "products";

export function isAlgoliaConfigured() {
  return Boolean(ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY);
}

let validated = false;

/**
 * Validates env once at app boot. Safe to call multiple times.
 */
export function validateEnv() {
  if (validated) return;
  validated = true;

  if (!readTrimmed("REACT_APP_API_URL") && !readTrimmed("VITE_API_URL")) {
    warnMissing(
      "REACT_APP_API_URL",
      `Fallback actif : ${API_URL}. Définir la variable du pod (env.js) ou du build.`
    );
  }

  if (!GOOGLE_CLIENT_ID) {
    warnMissing(
      "REACT_APP_GOOGLE_CLIENT_ID",
      "L'auth Google OAuth sera désactivée."
    );
  }

  if (!TINYMCE_API_KEY) {
    warnMissing(
      "REACT_APP_TINYMCE_KEY",
      "L'éditeur TinyMCE (documents admin) peut échouer."
    );
  }

  if (!RECAPTCHA_SITE_KEY) {
    warnMissing(
      "REACT_APP_RECAPTCHA_SITE_KEY",
      "Les formulaires publics (login, checkout, FAQ) ne pourront pas obtenir de token."
    );
  }

  if (PAYMENT_ONLINE_PROVIDER === "kkiapay" && !KKIAPAY_PUBLIC_KEY) {
    warnMissing(
      "REACT_APP_KKIAPAY_PUBLIC_KEY",
      "Requis lorsque REACT_APP_PAYMENT_ONLINE_PROVIDER=kkiapay."
    );
  }

  if (PAYMENT_ONLINE_PROVIDER === "fedapay" && !FEDAPAY_PUBLIC_KEY) {
    warnMissing(
      "REACT_APP_FEDAPAY_PUBLIC_KEY",
      "Requis lorsque REACT_APP_PAYMENT_ONLINE_PROVIDER=fedapay."
    );
  }

  if (!isAlgoliaConfigured()) {
    warnMissing(
      "REACT_APP_ALGOLIA_APP_ID / REACT_APP_ALGOLIA_SEARCH_KEY",
      "Recherche intelligente désactivée — fallback Mongo sur /categories."
    );
  }
}

validateEnv();
